/**
 * Error Handler Hook
 * 
 * Custom hook for handling errors with toast notifications
 * - Parse errors
 * - Display user-friendly messages
 * - Log errors for debugging
 * 
 * Requirements: 12.1, 12.2, 12.3, 12.5
 */

import { useCallback } from 'react';
import { useUIStore } from '../store/uiStore';
import { parseError, logError, getErrorMessage } from '../utils/errorHandling';

/**
 * Error handler hook
 * 
 * Provides a consistent way to handle errors across the application
 */
export function useErrorHandler() {
  const showToast = useUIStore(state => state.showToast);

  /**
   * Handle an error by parsing it, logging it, and showing a toast
   * Requirements: 12.1, 12.2, 12.3, 12.5
   */
  const handleError = useCallback((error: unknown, context?: string) => {
    const parsed = parseError(error);
    
    // Log error for debugging
    logError(parsed, context);

    // Show toast notification
    // Don't show toast for auth errors (handled by redirect)
    if (!parsed.isAuthError) {
      showToast('error', parsed.message, 5000);
    }

    return parsed;
  }, [showToast]);

  /**
   * Handle an error silently (log only, no toast)
   */
  const handleErrorSilently = useCallback((error: unknown, context?: string) => {
    const parsed = parseError(error);
    logError(parsed, context);
    return parsed;
  }, []);

  /**
   * Show a success message
   */
  const showSuccess = useCallback((message: string) => {
    showToast('success', message, 3000);
  }, [showToast]);

  /**
   * Show a warning message
   */
  const showWarning = useCallback((message: string) => {
    showToast('warning', message, 4000);
  }, [showToast]);

  /**
   * Show an info message
   */
  const showInfo = useCallback((message: string) => {
    showToast('info', message, 3000);
  }, [showToast]);

  return {
    handleError,
    handleErrorSilently,
    showSuccess,
    showWarning,
    showInfo,
    getErrorMessage,
  };
}

export default useErrorHandler;
